import { getPlayerName as getStoredPlayerName, savePlayerName, getPlayerFingerprint, sanitizePlayerName } from '../../shared/game-identity.js';
import { createClient } from '@supabase/supabase-js';
import { CONFIG } from '../config.js';
import { TOTAL_PROBLEMS } from './constants.js';
import { dom } from './dom.js';
import { goToLevel, startGame } from './game.js';
import { LEGACY_MIN_CROSSING_ANGLE_DEG } from './scenario.js';
import { getTotalScore } from './scoring.js';
import { state } from './state.js';
import { createSeededRandom } from './utils.js';

const DAILY_SEED_VERSION = 1;
const DAILY_TIME_ZONE = 'Europe/Zagreb';
const DAILY_GENERATOR_MIN_ANGLE_CHANGE_DATE = '2026-07-07';
const DAILY_RUN_PREFIX = 'qdm_daily_run_v1:';
const LEADERBOARD_HISTORY_KEY = 'qdmDailyLeaderboard';
const DAILY_LEVEL_TIME_LIMIT_BASE_MS = 30000;
const DAILY_LEVEL_TIME_LIMIT_STEP_MS = 5000;
const DAILY_TIMER_WARNING_MS = 5000;

let supabaseClient = null;
let leaderboardHistoryActive = false;
let dailyTimerId = null;

function getSupabaseClient() {
    if (!CONFIG.SUPABASE_URL || !CONFIG.SUPABASE_KEY) return null;
    if (!supabaseClient) {
        supabaseClient = createClient(CONFIG.SUPABASE_URL, CONFIG.SUPABASE_KEY);
    }
    return supabaseClient;
}

function getZagrebDateString(date = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: DAILY_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
}

function getTodayChallenge() {
    const challengeDate = getZagrebDateString();
    return {
        challengeDate,
        challengeId: `qdm-daily-${challengeDate}`,
    };
}

function getDailyRunKey(challengeId) {
    return `${DAILY_RUN_PREFIX}${challengeId}`;
}

function createDailyLeaderboardContext() {
    const challenge = getTodayChallenge();
    return {
        challengeId: challenge.challengeId,
        challengeDate: challenge.challengeDate,
        completed: false,
        leaderboardSubmittedAt: null,
    };
}

function scoresArrayToMap(levelScores) {
    return levelScores.reduce((scoresByLevel, score, index) => {
        if (Number.isFinite(score)) {
            scoresByLevel[index + 1] = score;
        }
        return scoresByLevel;
    }, {});
}

function getSubmittedLevels(levelScores) {
    return levelScores.map((_, index) => index + 1);
}

function saveRun(run) {
    localStorage.setItem(getDailyRunKey(run.challengeId), JSON.stringify(run));
}

export function loadTodayDailyRun() {
    const { challengeId } = getTodayChallenge();
    const raw = localStorage.getItem(getDailyRunKey(challengeId));
    if (!raw) return null;

    try {
        const run = JSON.parse(raw);
        return run?.challengeId === challengeId ? run : null;
    } catch (error) {
        console.warn('Could not parse daily QDM run:', error);
        return null;
    }
}

function createDailyRun(playerName) {
    const challenge = getTodayChallenge();
    const now = new Date().toISOString();
    return {
        version: 1,
        seedVersion: DAILY_SEED_VERSION,
        challengeId: challenge.challengeId,
        challengeDate: challenge.challengeDate,
        playerName,
        playerFingerprint: getPlayerFingerprint(),
        startedAt: now,
        currentLevel: 1,
        currentLevelStartedAt: null,
        levelScores: [],
        answers: [],
        totalScore: 0,
        durationMs: null,
        completed: false,
        completedAt: null,
        leaderboardEntryId: null,
        leaderboardSubmittedAt: null,
        leaderboardSubmitError: null,
    };
}

function getDailyLoadLevel(run) {
    if (run.completed) return TOTAL_PROBLEMS;
    return Math.min(Math.max(run.currentLevel || 1, 1), TOTAL_PROBLEMS);
}

function createDailyScenarioRandom(level) {
    return createSeededRandom(`${state.dailyRun.challengeId}:v${DAILY_SEED_VERSION}:level-${level}`);
}

function getDailyScenarioOptions(run) {
    // Preserve the already-live 2026-07-06 daily challenge; remove after 2026-07-06 has passed.
    if (run.challengeDate < DAILY_GENERATOR_MIN_ANGLE_CHANGE_DATE) {
        return { minimumCrossingAngleDeg: LEGACY_MIN_CROSSING_ANGLE_DEG };
    }
    return null;
}

function getDailyLevelTimeLimitMs(level) {
    return DAILY_LEVEL_TIME_LIMIT_BASE_MS + ((level - 1) * DAILY_LEVEL_TIME_LIMIT_STEP_MS);
}

function formatDailyTimer(remainingMs) {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function setDailySubmitWarning(active) {
    dom.submitBtn.classList.toggle('game-controls__submit--danger', active);
}

function resetDailyTimerProgress() {
    dom.timerProgress.classList.add('hidden');
    dom.timerProgressFill.style.transform = 'scaleX(1)';
    dom.timerProgressFill.classList.remove(
        'timer-progress__fill--warning',
        'timer-progress__fill--danger',
    );
}

function updateDailyTimerProgress(remainingMs, limitMs) {
    const ratio = Math.max(0, Math.min(1, remainingMs / limitMs));
    dom.timerProgress.classList.remove('hidden');
    dom.timerProgressFill.style.transform = `scaleX(${ratio})`;
    dom.timerProgressFill.classList.toggle('timer-progress__fill--warning', remainingMs > DAILY_TIMER_WARNING_MS && ratio <= 0.25);
    dom.timerProgressFill.classList.toggle('timer-progress__fill--danger', remainingMs <= DAILY_TIMER_WARNING_MS);
}

function stopDailyTimer() {
    if (dailyTimerId !== null) {
        window.clearInterval(dailyTimerId);
        dailyTimerId = null;
    }
    setDailySubmitWarning(false);
}

function hideDailyTimer() {
    stopDailyTimer();
    if (state.gameMode !== 'time') {
        dom.timeStatus.hidden = true;
        resetDailyTimerProgress();
    }
}

function getDailyRemainingMs(run) {
    if (!run?.currentLevelStartedAt) return getDailyLevelTimeLimitMs(run?.currentLevel || 1);
    const elapsedMs = Date.now() - new Date(run.currentLevelStartedAt).getTime();
    return Math.max(0, getDailyLevelTimeLimitMs(run.currentLevel) - elapsedMs);
}

function renderDailyTimer(run) {
    const remainingMs = getDailyRemainingMs(run);
    const limitMs = getDailyLevelTimeLimitMs(run.currentLevel);
    dom.timeStatus.hidden = false;
    dom.timeTimer.textContent = formatDailyTimer(remainingMs);
    updateDailyTimerProgress(remainingMs, limitMs);
    setDailySubmitWarning(remainingMs > 0 && remainingMs <= DAILY_TIMER_WARNING_MS);
}

function autoSubmitDailyAnswer() {
    const submitted = dom.slider.classList.contains('submitted');
    if (state.gameMode !== 'daily' || state.dailyCompleted || submitted) return;

    stopDailyTimer();
    dom.submitBtn.disabled = false;
    dom.submitBtn.click();
}

function tickDailyTimer() {
    const run = state.dailyRun;
    if (state.gameMode !== 'daily' || !run || run.completed) {
        hideDailyTimer();
        return;
    }

    renderDailyTimer(run);
    if (getDailyRemainingMs(run) <= 0) {
        autoSubmitDailyAnswer();
    }
}

export function startDailyLevelTimer() {
    const run = state.dailyRun;
    if (state.gameMode !== 'daily' || !run || run.completed) {
        hideDailyTimer();
        return;
    }

    stopDailyTimer();
    if (!run.currentLevelStartedAt) {
        run.currentLevelStartedAt = new Date().toISOString();
        saveRun(run);
    }

    renderDailyTimer(run);
    if (getDailyRemainingMs(run) <= 0) {
        window.setTimeout(autoSubmitDailyAnswer, 0);
        return;
    }

    dailyTimerId = window.setInterval(tickDailyTimer, 250);
}

export function pauseDailyTimer() {
    hideDailyTimer();
}

function syncDailyButton(active) {
    dom.dailyChallengeBtn.classList.toggle('game-header__daily-btn--active', active);
}

export function startDailyRun(run) {
    state.dailyRun = run;
    syncDailyButton(true);
    startGame({
        mode: 'daily',
        dailyRun: run,
        dailyCompleted: run.completed,
        currentProblem: getDailyLoadLevel(run),
        createScenarioRandom: createDailyScenarioRandom,
        scenarioOptions: getDailyScenarioOptions(run),
        scoresByLevel: scoresArrayToMap(run.levelScores || []),
        submittedLevels: getSubmittedLevels(run.levelScores || []),
    });
    startDailyLevelTimer();
}

function openNameModal() {
    dom.dailyNameInput.value = getStoredPlayerName();
    dom.dailyNameModal.classList.remove('hidden');
    dom.dailyNameInput.focus();
    dom.dailyNameInput.select();
}

function closeNameModal() {
    dom.dailyNameModal.classList.add('hidden');
}

function isLeaderboardModalOpen() {
    return !dom.dailyLeaderboardModal.classList.contains('hidden');
}

function openLeaderboardModal() {
    dom.dailyLeaderboardModal.classList.remove('hidden');
    if (!leaderboardHistoryActive) {
        window.history.pushState(
            { ...(window.history.state || {}), [LEADERBOARD_HISTORY_KEY]: true },
            '',
            window.location.href,
        );
        leaderboardHistoryActive = true;
    }
}

function closeLeaderboardModal() {
    dom.dailyLeaderboardModal.classList.add('hidden');
    leaderboardHistoryActive = false;
}

function closeDailyLeaderboard() {
    closeLeaderboardModal();
}

export function exitDailyChallenge() {
    hideDailyTimer();
    closeLeaderboardModal();
    syncDailyButton(false);
    startGame();
}

function handleLeaderboardCloseClick() {
    if (leaderboardHistoryActive) {
        window.history.back();
        return;
    }

    closeDailyLeaderboard();
}

function handleHistoryNavigation() {
    if (!leaderboardHistoryActive || !isLeaderboardModalOpen()) return;
    closeDailyLeaderboard();
}

function setLeaderboardStatus(message, tone = 'neutral') {
    dom.dailySubmitStatus.textContent = message;
    dom.dailySubmitStatus.classList.toggle('text-red-300', tone === 'error');
    dom.dailySubmitStatus.classList.toggle('text-green-300', tone === 'success');
}

function formatDuration(durationMs) {
    if (!Number.isFinite(durationMs)) return '';
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function renderLeaderboardRows(rows) {
    dom.dailyLeaderboardList.innerHTML = '';
    rows.forEach((row) => {
        const item = document.createElement('li');
        item.className = 'daily-leaderboard-row';

        const rank = document.createElement('span');
        rank.className = 'daily-leaderboard-rank';
        rank.textContent = `#${row.leaderboard_rank}`;

        const player = document.createElement('div');
        const name = document.createElement('div');
        name.className = 'daily-leaderboard-name';
        name.textContent = row.player_name;
        const meta = document.createElement('div');
        meta.className = 'daily-leaderboard-meta';
        const duration = formatDuration(row.duration_ms);
        meta.textContent = duration ? `${duration} total` : 'No time';
        player.append(name, meta);

        const score = document.createElement('span');
        score.className = 'daily-leaderboard-score';
        score.textContent = row.score;

        item.append(rank, player, score);
        dom.dailyLeaderboardList.appendChild(item);
    });
}

function syncDailyPlayButton() {
    const canPlayToday = !loadTodayDailyRun();
    dom.dailyLeaderboardPlayBtn.hidden = !canPlayToday;
    dom.dailyLeaderboardCloseBtn.classList.toggle('w-full', !canPlayToday);
    dom.dailyLeaderboardCloseBtn.classList.toggle('w-1/2', canPlayToday);
}

async function fetchDailyLeaderboard(run) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase is not configured.');

    const { data, error } = await client
        .from('qdm_daily_leaderboard')
        .select('leaderboard_rank, player_name, score, duration_ms, created_at')
        .eq('challenge_id', run.challengeId)
        .order('leaderboard_rank', { ascending: true })
        .limit(10);

    if (error) throw error;
    return data || [];
}

async function submitDailyRunIfNeeded(run) {
    if (!run.completed || run.leaderboardSubmittedAt) return run;

    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase is not configured.');

    const payload = {
        challenge_type: 'daily',
        challenge_id: run.challengeId,
        challenge_date: run.challengeDate,
        seed_version: run.seedVersion,
        player_name: run.playerName,
        player_fingerprint: run.playerFingerprint,
        score: run.totalScore,
        duration_ms: run.durationMs,
        completed_levels: run.levelScores.length,
        level_scores: run.levelScores,
        client_version: 'qdm-daily-v1',
    };

    const { data, error } = await client
        .from('qdm_leaderboard_entries')
        .insert(payload)
        .select('id, created_at')
        .single();

    if (error) {
        if (error.code === '23505') {
            run.leaderboardSubmittedAt = new Date().toISOString();
            run.leaderboardSubmitError = null;
            saveRun(run);
            return run;
        }
        run.leaderboardSubmitError = error.message;
        saveRun(run);
        throw error;
    }

    run.leaderboardEntryId = data?.id || null;
    run.leaderboardSubmittedAt = data?.created_at || new Date().toISOString();
    run.leaderboardSubmitError = null;
    saveRun(run);
    return run;
}

export async function showDailyLeaderboard(run = state.dailyRun) {
    if (!run) run = loadTodayDailyRun() || createDailyLeaderboardContext();

    dom.dailyLeaderboardDate.textContent = run.challengeDate;
    dom.dailyLeaderboardList.innerHTML = '';
    syncDailyPlayButton();
    openLeaderboardModal();

    try {
        if (run.completed && !run.leaderboardSubmittedAt) {
            setLeaderboardStatus('Submitting score...');
            run = await submitDailyRunIfNeeded(run);
            state.dailyRun = run;
        }

        const rows = await fetchDailyLeaderboard(run);
        setLeaderboardStatus(run.completed ? 'Score submitted.' : 'Daily scores.', run.completed ? 'success' : 'neutral');
        renderLeaderboardRows(rows);
    } catch (error) {
        console.error('Daily leaderboard error:', error);
        setLeaderboardStatus('Leaderboard unavailable.', 'error');
    }
}

export function recordDailyAnswer({ level, rawValue, score }) {
    const run = state.dailyRun;
    if (!run || run.completed) return false;
    if (level !== run.currentLevel) return false;

    run.levelScores[level - 1] = score;
    run.answers[level - 1] = {
        level,
        rawValue,
        score,
        submittedAt: new Date().toISOString(),
    };
    run.totalScore = getTotalScore();

    if (level >= TOTAL_PROBLEMS) {
        run.completed = true;
        run.completedAt = new Date().toISOString();
        run.currentLevel = TOTAL_PROBLEMS;
        run.currentLevelStartedAt = null;
        run.durationMs = Math.min(
            3600000,
            Math.max(1000, new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()),
        );
        state.dailyCompleted = true;
    } else {
        run.currentLevel = level + 1;
        run.currentLevelStartedAt = null;
    }

    state.dailyRun = run;
    saveRun(run);
    return run.completed;
}

export function goToNextDailyLevel() {
    const run = state.dailyRun;
    if (!run || run.completed) return;
    goToLevel(run.currentLevel);
    startDailyLevelTimer();
}

function handleDailyButtonClick() {
    showDailyLeaderboard(loadTodayDailyRun() || createDailyLeaderboardContext());
}

function beginDailyRun(playerName) {
    savePlayerName(playerName);
    const run = createDailyRun(playerName);
    saveRun(run);
    closeLeaderboardModal();
    closeNameModal();
    startDailyRun(run);
}

function handleDailyPlayClick() {
    if (loadTodayDailyRun()) {
        syncDailyPlayButton();
        return;
    }

    const playerName = sanitizePlayerName(getStoredPlayerName());
    if (!playerName) {
        openNameModal();
        return;
    }

    beginDailyRun(playerName);
}

function handleNameSubmit(event) {
    event.preventDefault();
    const playerName = sanitizePlayerName(dom.dailyNameInput.value);
    if (!playerName) {
        dom.dailyNameInput.focus();
        return;
    }

    beginDailyRun(playerName);
}

export function initDailyChallenge() {
    dom.dailyChallengeBtn.addEventListener('click', handleDailyButtonClick);
    dom.dailyNameForm.addEventListener('submit', handleNameSubmit);
    dom.dailyNameCancelBtn.addEventListener('click', closeNameModal);
    dom.dailyLeaderboardCloseBtn.addEventListener('click', handleLeaderboardCloseClick);
    dom.dailyLeaderboardPlayBtn.addEventListener('click', handleDailyPlayClick);
    window.addEventListener('popstate', handleHistoryNavigation);
}

export function resumeDailyChallengeIfNeeded() {
    const run = loadTodayDailyRun();
    if (!run) return false;

    startDailyRun(run);
    if (run.completed) {
        showDailyLeaderboard(run);
    }
    return true;
}
